import React, { useState, useMemo } from 'react';
import ResultFigure from './shared/ResultFigure';
import ExploreControls, { useExploration } from './shared/ExploreControls';
import { BaseModel } from '../types/model';
import {
  createBarChartConfig,
  createHeatmapConfig,
} from '../utils/chartConfigs';
import {
  getArrayShape,
  alphabeticalPermutation,
  permuteArray1D,
  permuteRows2D,
  permuteCols2D,
  safeRatio,
} from '../utils/dataProcessing';
import VariableSelect, { VariableOptionGroup } from './shared/VariableSelect';
import OutputVariableSelect from './shared/OutputVariableSelect';
import { groupVariables, modelView } from '../utils/variableMetadata';

interface VariableVisualizationProps {
  model: BaseModel;
  mode: 'single' | 'comparison';
  initialVariable?: string;
  lockParameter?: boolean;
}

const VariableVisualization: React.FC<VariableVisualizationProps> = ({
  model: sourceModel,
  mode,
  initialVariable = '',
  lockParameter = false,
}) => {
  const model = useMemo(() => modelView(sourceModel), [sourceModel]);
  const [selectedVariable, setSelectedVariable] = useState<string>(initialVariable);
  const [dataSource, setDataSource] = useState<'sol' | 'params'>('params');

  // Fix 3: memoize country sort permutation and sorted labels.
  // These are placed before any conditional returns (Rules of Hooks).
  // model.country_list is stable once a model is loaded so these only
  // recompute on model change.
  const countryPerm = useMemo(
    () => alphabeticalPermutation(model.country_list),
    [model.country_list],
  );
  const sortedCountries = useMemo(
    () => countryPerm.map(i => model.country_list[i]),
    [countryPerm, model.country_list],
  );

  // Get variables from params (if available) and sol
  const hasParams = model.params && Object.keys(model.params).length > 0;

  // Filter params to only include numeric scalars/arrays (exclude string lists).
  // Some calibrated params are plain scalars (e.g. Furusawa2023's
  // g/beta/xi discount-rate constants) — these render as a single
  // number via the ndim===0 branch below, same as scalar sol variables.
  const validParamsVars = useMemo(() => {
    if (!hasParams) return [];
    return Object.keys(model.params).filter(k => {
      if (lockParameter && k !== initialVariable) return false;
      const val = model.params[k];
      if (typeof val === 'number') return true;
      if (Array.isArray(val) && val.length > 0) {
        // Check if first element is a number or array of numbers
        const first = val[0];
        return typeof first === 'number' || Array.isArray(first);
      }
      return false;
    });
  }, [hasParams, model.params, lockParameter, initialVariable]);

  const solVars = useMemo(() => Object.keys(model.sol), [model.sol]);

  // Use params only if available and valid, otherwise fallback to sol
  const effectiveDataSource = hasParams && validParamsVars.length > 0 ? (lockParameter ? 'params' : dataSource) : 'sol';
  const variables = effectiveDataSource === 'params' ? validParamsVars : solVars;

  // Memoize the shape-grouped picker options — only needs to recompute when
  // the variable set, data source, or model identity actually changes.
  const variableGroups = useMemo(() => {
    const groups: VariableOptionGroup[] = groupVariables(
      variables, model.input_metadata, model.variable_display_names,
    );
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables, effectiveDataSource, model]);

  React.useEffect(() => {
    if (variables.length > 0 && !variables.includes(selectedVariable)) {
      setSelectedVariable(variables[0]);
    }
  }, [variables, selectedVariable, effectiveDataSource]);

  const selectedObject = selectedVariable.split('.').pop();

  const exploration = useExploration(`${effectiveDataSource}/${selectedObject}`, getArrayShape(effectiveDataSource === 'params' ? model.params[selectedVariable] : model.sol[selectedVariable]));
  const { fixed: fixedDims, values: dimValues } = exploration;

  if (!selectedVariable || variables.length === 0) {
    return <div style={{ padding: 'var(--spacing-lg)', textAlign: 'center', color: 'var(--color-text-subtle)' }}>No data available</div>;
  }

  const isCF = effectiveDataSource === 'sol' && selectedVariable.startsWith('hats.');
  const displayAsCF = isCF;
  const inputMetadata = model.input_metadata[selectedVariable];
  const variableLabel = inputMetadata.meaning;
  const isCountryAxis = (_size: number, axis: number) => inputMetadata.axes[axis]?.kind === 'country';
  const dimensions = (shape: number[]) => inputMetadata.axes.map((axis, i) => ({ name: axis.name, size: shape[i] }));
  const getLabels = (dimSize: number, axis: number): string[] => {
    if (isCountryAxis(dimSize, axis)) {
      return sortedCountries;
    } else if (inputMetadata.axes[axis]?.kind === 'sector') {
      return model.sector_list;
    }
    return Array.from({ length: dimSize }, (_, i) => `Item ${i}`);
  };

  /**
   * Return the sort permutation that was applied when getLabels returned countries.
   * Returns null (identity) for non-country dimensions.
   */
  const getDimPerm = (dimSize: number, axis: number): number[] | null => {
    if (isCountryAxis(dimSize, axis)) return countryPerm;
    return null;
  };

  const data = effectiveDataSource === 'params'
    ? model.params[selectedVariable]
    : model.sol[selectedVariable];

  // Safety check - if data is undefined/null, show error
  if (data === undefined || data === null) {
    return <div style={{ padding: 'var(--spacing-lg)', textAlign: 'center', color: 'var(--color-text-subtle)' }}>Variable "{selectedVariable}" not found</div>;
  }
  const shape = getArrayShape(data);
  const ndim = shape.length;

  const computeRatio1D = (cf: number[], baseline: number[]): number[] =>
    cf.map((v, i) => safeRatio(v, baseline[i]));

  const computeRatio2D = (cf: number[][], baseline: number[][]): number[][] =>
    cf.map((row, i) => row.map((v, j) => safeRatio(v, baseline[i]?.[j] ?? 0)));

  const renderChart = () => {
    if (ndim === 0) {
      const value = data as number;
      const baselineValue = isCF && model.baseline_sol
        ? (model.baseline_sol[selectedVariable] as number)
        : null;
      const displayValue = (isCF && baselineValue !== null)
        ? safeRatio(value, baselineValue) * 100
        : value;
      return (
        <div style={styles.scalarDisplay}>
          <div style={styles.scalarValue}>
            {isCF ? `${displayValue >= 0 ? '+' : ''}${displayValue.toFixed(2)}%` : displayValue.toFixed(4)}
          </div>
          <div style={styles.scalarLabel}>{variableLabel}</div>
        </div>
      );
    } else if (ndim === 1) {
      const perm = getDimPerm(shape[0], 0);
      const labels = getLabels(shape[0], 0);
      const rawValues = data as number[];

      const displayValues = (() => {
        if (displayAsCF && model.baseline_sol) {
          const baseRaw = model.baseline_sol[selectedVariable] as number[];
          const ratio = computeRatio1D(rawValues, baseRaw);
          return perm ? permuteArray1D(ratio, perm) : ratio;
        }
        return perm ? permuteArray1D(rawValues, perm) : rawValues;
      })();

      if (fixedDims.includes(0)) {
        const index = dimValues[0] || 0;
        const value = displayAsCF ? displayValues[index] * 100 : displayValues[index];
        return <div className="single-value-results"><strong>{Number.isFinite(value) ? value.toLocaleString(undefined, { maximumSignificantDigits: 8 }) : '—'}{displayAsCF && '%'}</strong><p>{labels[index]} · {variableLabel}</p></div>;
      }

      return (
        <div className="chart-container">
          <ResultFigure
            option={createBarChartConfig(labels, displayValues, variableLabel, variableLabel, displayAsCF)}
            style={{ height: 'clamp(360px, 52vh, 520px)' }}
          />
        </div>
      );
    } else if (ndim === 2) {
      return render2DVariable();
    } else if (ndim === 3) {
      return render3DVariable();
    }

    return <div style={styles.infoBox}>Visualization not available for shape {shape.join(' × ')}</div>;
  };

  const render2DVariable = () => {
    const shape = getArrayShape(data);
    const dimInfo = dimensions(shape);
    const perm0 = getDimPerm(shape[0], 0);
    const perm1 = getDimPerm(shape[1], 1);
    const labels0 = getLabels(shape[0], 0);
    const labels1 = getLabels(shape[1], 1);
    const cfValues = data as number[][];

    // Compute ratio matrix once if in CF mode
    const baseValues2D = isCF && model.baseline_sol
      ? (model.baseline_sol[selectedVariable] as number[][])
      : null;
    const ratioValues2D = baseValues2D ? computeRatio2D(cfValues, baseValues2D) : null;
    // Use ratio for display when available, otherwise use raw CF values
    const values = ratioValues2D ?? cfValues;

    // Translate a sorted-index into the original data index for slicing
    const originalIdx = (dimIdx: number, sortedIdx: number): number => {
      const perm = dimIdx === 0 ? perm0 : perm1;
      return perm ? perm[sortedIdx] : sortedIdx;
    };

    const freeDims = [0, 1].filter(d => !fixedDims.includes(d));
    const numFreeDims = freeDims.length;


    if (numFreeDims === 2) {
      // Show 2D heatmap — permute both axes so display is alphabetical
      const displayValues = (() => {
        let v = values;
        if (perm0) v = permuteRows2D(v, perm0);
        if (perm1) v = permuteCols2D(v, perm1);
        return v;
      })();

      return (
        <div>
          <div className="chart-container">
            <ResultFigure
              option={createHeatmapConfig(
                labels1,
                labels0,
                displayValues,
                variableLabel,
                dimInfo[1]?.name || 'Columns',
                dimInfo[0]?.name || 'Rows',
                isCF
              )}
              style={{ height: 'clamp(420px, 60vh, 620px)' }}
            />
          </div>
        </div>
      );
    } else if (numFreeDims === 1) {
      // Show 1D bar chart
      const freeDim = freeDims[0];
      const fixedDim = fixedDims[0];
      const freeLabels = freeDim === 0 ? labels0 : labels1;
      const sortedFixedIdx = dimValues[fixedDim] || 0;
      const fixedLabel = (fixedDim === 0 ? labels0 : labels1)[sortedFixedIdx];
      // Translate sorted index → original data index for slicing
      const origFixedIdx = originalIdx(fixedDim, sortedFixedIdx);

      let rawSlice: number[];
      if (fixedDim === 0) {
        rawSlice = values[origFixedIdx];
      } else {
        rawSlice = values.map(row => row[origFixedIdx]);
      }
      // Permute the free dimension labels/data to alphabetical order
      const freePerm = freeDim === 0 ? perm0 : perm1;
      const sliceData = freePerm ? permuteArray1D(rawSlice, freePerm) : rawSlice;

      return (
        <div>
          <div style={styles.dimensionInfo}>
            <div style={styles.dimensionInfoRow}>
              <span style={styles.dimensionExplanation}>
                Viewing: <strong>{dimInfo[freeDim]?.name}</strong> — Fixed: <strong>{dimInfo[fixedDim]?.name} = {fixedLabel}</strong>
              </span>
            </div>
          </div>

          <div className="chart-container">
            <ResultFigure
              option={createBarChartConfig(
                freeLabels,
                sliceData,
                `${variableLabel} [${dimInfo[fixedDim]?.name}=${fixedLabel}]`,
                inputMetadata?.units || 'Value',
                isCF
              )}
              style={{ height: 'clamp(420px, 60vh, 620px)' }}
            />
          </div>
        </div>
      );
    } else {
      // numFreeDims === 0: Show scalar
      // Both dims are fixed; translate sorted indices to original data indices
      const orig0 = originalIdx(0, dimValues[0] || 0);
      const orig1 = originalIdx(1, dimValues[1] || 0);
      const value = values[orig0][orig1];
      const label0 = labels0[dimValues[0] || 0];
      const label1 = labels1[dimValues[1] || 0];

      return (
        <div>
          <div style={styles.dimensionInfo}>
            <div style={styles.dimensionInfoRow}>
              <span style={styles.dimensionExplanation}>
                {dimInfo[0]?.name} = <strong>{label0}</strong> — {dimInfo[1]?.name} = <strong>{label1}</strong>
              </span>
            </div>
          </div>

          <div style={styles.scalarDisplay}>
            <div style={styles.scalarValue}>
              {isCF ? `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)}%` : value.toFixed(6)}
            </div>
            <div style={styles.scalarLabel}>
              {variableLabel}[{label0}, {label1}]
            </div>
          </div>
        </div>
      );
    }
  };

  const render3DVariable = () => {
    const shape = getArrayShape(data);
    const dimInfo = dimensions(shape);
    const perm0 = getDimPerm(shape[0], 0);
    const perm1 = getDimPerm(shape[1], 1);
    const perm2 = getDimPerm(shape[2], 2);
    const perms = [perm0, perm1, perm2];
    const labels0 = getLabels(shape[0], 0);
    const labels1 = getLabels(shape[1], 1);
    const labels2 = getLabels(shape[2], 2);
    const cfValues3D = data as number[][][];

    // Compute 3D ratio when in CF mode.
    // Guard: if the key is missing from baseline_sol, fall back to raw values.
    const baseValues3D = (isCF && model.baseline_sol)
      ? (model.baseline_sol[selectedVariable] as number[][][] | undefined) ?? null
      : null;
    const values = baseValues3D
      ? cfValues3D.map((layer, i) =>
          layer.map((row, j) =>
            row.map((v, k) => safeRatio(v, baseValues3D[i]?.[j]?.[k] ?? 0))
          )
        )
      : cfValues3D;

    // Translate a sorted-index to original data index for a given dimension
    const origIdx3 = (dim: number, sortedIdx: number): number => {
      const p = perms[dim];
      return p ? p[sortedIdx] : sortedIdx;
    };

    const freeDims = [0, 1, 2].filter(d => !fixedDims.includes(d));
    const numFreeDims = freeDims.length;


    if (numFreeDims === 3) {
      // No dimensions fixed - can't visualize 3D directly
      return (
        <div>
          <div style={styles.dimensionInfo}>
            <div style={styles.dimensionInfoRow}>
              <span style={styles.dimensionExplanation}>Click at least one dimension badge to fix it and view a 2D slice</span>
            </div>
          </div>
          <div style={styles.infoBox}>
            Please fix at least one dimension to visualize this 3D array
          </div>
        </div>
      );
    } else if (numFreeDims === 2) {
      // Show 2D heatmap
      const fixedDim = fixedDims[0];
      const sortedFixedIdx = dimValues[fixedDim] || 0;
      const origFixedIdx = origIdx3(fixedDim, sortedFixedIdx);
      const fixedLabel = (fixedDim === 0 ? labels0 : fixedDim === 1 ? labels1 : labels2)[sortedFixedIdx];

      // Determine which dimensions are free and get their labels
      let rawSlice: number[][];
      let xLabels: string[];
      let yLabels: string[];
      let xAxisName: string;
      let yAxisName: string;
      let yPerm: number[] | null;
      let xPerm: number[] | null;

      if (fixedDim === 0) {
        // Fix dim 0, show dims 1 x 2
        rawSlice = values[origFixedIdx];
        yLabels = labels1;
        xLabels = labels2;
        yAxisName = dimInfo[1]?.name || 'Dim 1';
        xAxisName = dimInfo[2]?.name || 'Dim 2';
        yPerm = perm1;
        xPerm = perm2;
      } else if (fixedDim === 1) {
        // Fix dim 1, show dims 0 x 2
        rawSlice = values.map(row => row[origFixedIdx]);
        yLabels = labels0;
        xLabels = labels2;
        yAxisName = dimInfo[0]?.name || 'Dim 0';
        xAxisName = dimInfo[2]?.name || 'Dim 2';
        yPerm = perm0;
        xPerm = perm2;
      } else {
        // Fix dim 2, show dims 0 x 1
        rawSlice = values.map(row => row.map(col => col[origFixedIdx]));
        yLabels = labels0;
        xLabels = labels1;
        yAxisName = dimInfo[0]?.name || 'Dim 0';
        xAxisName = dimInfo[1]?.name || 'Dim 1';
        yPerm = perm0;
        xPerm = perm1;
      }

      // Apply permutations to slice data for display
      let sliceData = rawSlice;
      if (yPerm) sliceData = permuteRows2D(sliceData, yPerm);
      if (xPerm) sliceData = permuteCols2D(sliceData, xPerm);

      return (
        <div>
          <div style={styles.dimensionInfo}>
            <div style={styles.dimensionInfoRow}>
              <span style={styles.dimensionExplanation}>
                Fixed: <strong>{dimInfo[fixedDim]?.name} = {fixedLabel}</strong> — Rows: <strong>{yAxisName}</strong> — Cols: <strong>{xAxisName}</strong>
              </span>
            </div>
          </div>

          <div className="chart-container">
            <ResultFigure
              option={createHeatmapConfig(
                xLabels,
                yLabels,
                sliceData,
                `${variableLabel} [${dimInfo[fixedDim]?.name}=${fixedLabel}]`,
                xAxisName,
                yAxisName,
                isCF
              )}
              style={{ height: 'clamp(420px, 60vh, 620px)' }}
            />
          </div>
        </div>
      );
    } else if (numFreeDims === 1) {
      // Show 1D bar chart
      const freeDim = freeDims[0];
      const freeLabels = freeDim === 0 ? labels0 : freeDim === 1 ? labels1 : labels2;

      // Fixed dimension labels and original indices
      const fixedLabels = fixedDims.map(d => {
        const lbls = d === 0 ? labels0 : d === 1 ? labels1 : labels2;
        return lbls[dimValues[d] || 0];
      });
      const origFixed = fixedDims.map(d => origIdx3(d, dimValues[d] || 0));

      let rawSlice: number[];
      if (fixedDims.includes(0) && fixedDims.includes(1)) {
        // Fix dims 0 and 1, show dim 2
        rawSlice = values[origFixed[0]][origFixed[1]];
      } else if (fixedDims.includes(0) && fixedDims.includes(2)) {
        // Fix dims 0 and 2, show dim 1
        rawSlice = values[origFixed[0]].map(row => row[origFixed[1]]);
      } else {
        // Fix dims 1 and 2, show dim 0
        rawSlice = values.map(layer => layer[origFixed[0]][origFixed[1]]);
      }

      const freePerm = perms[freeDim];
      const sliceData = freePerm ? permuteArray1D(rawSlice, freePerm) : rawSlice;

      const fixedDesc = fixedDims.map((d, i) => `${dimInfo[d]?.name}=${fixedLabels[i]}`).join(', ');

      return (
        <div>
          <div style={styles.dimensionInfo}>
            <div style={styles.dimensionInfoRow}>
              <span style={styles.dimensionExplanation}>
                Viewing: <strong>{dimInfo[freeDim]?.name}</strong> — Fixed: <strong>{fixedDesc}</strong>
              </span>
            </div>
          </div>

          <div className="chart-container">
            <ResultFigure
              option={createBarChartConfig(
                freeLabels,
                sliceData,
                `${variableLabel} [${fixedDesc}]`,
                inputMetadata?.units || 'Value',
                isCF
              )}
              style={{ height: 'clamp(420px, 60vh, 620px)' }}
            />
          </div>
        </div>
      );
    } else {
      // numFreeDims === 0: Show scalar (all 3 dims fixed)
      const orig0 = origIdx3(0, dimValues[0] || 0);
      const orig1 = origIdx3(1, dimValues[1] || 0);
      const orig2 = origIdx3(2, dimValues[2] || 0);
      const value = values[orig0][orig1][orig2];
      const label0 = labels0[dimValues[0] || 0];
      const label1 = labels1[dimValues[1] || 0];
      const label2 = labels2[dimValues[2] || 0];

      return (
        <div>
          <div style={styles.dimensionInfo}>
            <div style={styles.dimensionInfoRow}>
              <span style={styles.dimensionExplanation}>
                {dimInfo[0]?.name} = <strong>{label0}</strong> — {dimInfo[1]?.name} = <strong>{label1}</strong> — {dimInfo[2]?.name} = <strong>{label2}</strong>
              </span>
            </div>
          </div>

          <div style={styles.scalarDisplay}>
            <div style={styles.scalarValue}>
              {isCF ? `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)}%` : value.toFixed(6)}
            </div>
            <div style={styles.scalarLabel}>
              {variableLabel}[{label0}, {label1}, {label2}]
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="variable-explorer">
      <aside className="explorer-settings">
      <div className="variable-toolbar" style={styles.header}>
        <div className="variable-selectors" style={styles.selectorGroup}>
          {/* Data source toggle - only show if params available */}
          {!lockParameter && hasParams && validParamsVars.length > 0 && mode === 'single' && (
            <div className="variable-source-toggle" style={styles.sourceToggle}>
              <button
                style={{
                  ...styles.sourceBtn,
                  ...(effectiveDataSource === 'params' ? styles.sourceBtnActive : {}),
                }}
                aria-pressed={effectiveDataSource === 'params'}
                onClick={() => setDataSource('params')}
              >
                Inputs
              </button>
              <button
                style={{
                  ...styles.sourceBtn,
                  ...(effectiveDataSource === 'sol' ? styles.sourceBtnActive : {}),
                }}
                aria-pressed={effectiveDataSource === 'sol'}
                onClick={() => setDataSource('sol')}
              >
                Equilibrium outputs
              </button>
            </div>
          )}
          {!lockParameter && (effectiveDataSource === 'sol'
            ? <OutputVariableSelect value={selectedVariable} onChange={setSelectedVariable}
                paths={solVars} metadata={model.input_metadata} names={model.variable_display_names} />
            : <label className="variable-picker" style={styles.selectLabel}>
            <span>Variable</span>
            <div className="variable-picker-control">
              <VariableSelect
                value={selectedVariable}
                onChange={setSelectedVariable}
                minWidth="min(280px, 100%)"
                groups={variableGroups}
              />
            </div>
          </label>)}
        </div>
      </div>

      <ExploreControls dimensions={dimensions(shape)} fixed={fixedDims} values={dimValues}
        labels={shape.map((size, axis) => getLabels(size, axis))} onView={exploration.onView}
        onValue={exploration.onValue} />
      </aside>
      <section className="explorer-result" aria-label="Selected data">
      <div style={styles.chartArea}>{renderChart()}</div>
      {inputMetadata && <p className="figure-caption">
        Units: {inputMetadata.units}. {!lockParameter && inputMetadata.calibration_url && <a href={inputMetadata.calibration_url}>Data source and construction</a>}
      </p>}
      </section>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 0 12px',
    backgroundColor: 'transparent',
    borderBottom: '1px solid var(--color-border)',
    flexWrap: 'wrap',
    gap: '12px',
  },
  selectorGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-lg)',
    flexWrap: 'wrap',
  },
  sourceToggle: {
    display: 'flex',
    backgroundColor: '#e5e7eb',
    borderRadius: 'var(--radius-sm)',
    padding: '2px',
  },
  sourceBtn: {
    padding: '6px 12px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--color-text-subtle)',
    borderRadius: 'var(--radius-sm)',

  },
  sourceBtnActive: {
    backgroundColor: 'white',
    color: 'var(--color-primary)',
    boxShadow: 'none',
  },
  selectLabel: {
    fontWeight: 500,
    color: 'var(--color-text)',
    fontSize: '14px',
  },
  chartArea: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-md)',
  },
  scalarDisplay: {
    padding: '60px',
    textAlign: 'center',
    border: '1px dashed var(--color-border)',
    borderRadius: 'var(--radius-md)',
  },
  scalarValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: 'var(--color-primary)',
  },
  scalarLabel: {
    color: 'var(--color-text-subtle)',
    marginTop: 'var(--spacing-sm)',
  },
  infoBox: {
    padding: 'var(--spacing-lg)',
    textAlign: 'center',
    color: 'var(--color-text-subtle)',
    backgroundColor: 'var(--color-bg)',
    borderRadius: 'var(--radius-md)',
  },
  dimensionInfo: {
    borderBottom: '1px solid #e2e8f0',
    padding: '8px 0',
    marginBottom: '6px',
  },
  dimensionInfoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  dimensionExplanation: {
    fontSize: '12px',
    color: '#64748b',
  },
};

export default VariableVisualization;
